<template>
    <div ref="editor" />
</template>

<script>
import Quill from 'quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.bubble.css'
import 'quill/dist/quill.snow.css'

export default {
    props: {
        toolbar: {
            type: Array,
            default: () => [
                [{ header: 1 }, { header: 2 }],
                ['bold', 'italic', 'underline', 'link'],
                ['image', 'code-block'],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }]
            ]
        },
        content: {
            type: Object,
            default: null
        },
        theme: {
            type: String,
            default: 'snow'
        },
        readOnly: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:content'],
    data () {
        return {
            isUpdating: false,
            isInitializing: false
        }
    },
    computed: {
        currentContent () {
            return this.content
        }
    },
    watch: {
        currentContent: {
            handler (newContent) {
                if (this.editor && newContent && !this.isUpdating && !this.isInitializing) {
                    try {
                        this.editor.setContents(newContent)
                    } catch (err) {
                        console.warn('Error setting editor content:', err)
                    }
                }
            },
            deep: true
        }
    },
    mounted () {
        this.initializeEditor()
    },
    beforeUnmount () {
        this.destroyEditor()
    },
    methods: {
        initializeEditor () {
            // Prevent multiple initializations
            if (this.editor || this.isInitializing || !this.$refs.editor) {
                return
            }

            this.isInitializing = true

            try {
                this.editor = new Quill(this.$refs.editor, {
                    modules: {
                        toolbar: this.toolbar,
                    },
                    theme: this.theme,
                    readOnly: this.readOnly
                })

                // Set initial content
                const initialContent = this.currentContent
                if (initialContent) {
                    this.editor.setContents(initialContent)
                }

                // Bind event listener with proper context
                this.editor.on('text-change', this.handleTextChange)
            } catch (err) {
                console.error('Error initializing Quill editor:', err)
            } finally {
                this.isInitializing = false
            }
        },

        destroyEditor () {
            if (this.editor) {
                try {
                    // Remove event listeners
                    this.editor.off('text-change', this.handleTextChange)

                    // Clear the editor container
                    if (this.$refs.editor) {
                        this.$refs.editor.innerHTML = ''
                    }
                } catch (err) {
                    console.warn('Error destroying editor:', err)
                } finally {
                    this.editor = null
                }
            }
        },

        handleTextChange () {
            if (!this.isUpdating && !this.isInitializing) {
                try {
                    this.update()
                } catch (err) {
                    console.warn('Quill update error:', err)
                }
            }
        },

        update () {
            if (!this.editor || this.isUpdating) {
                return
            }

            this.isUpdating = true

            try {
                const content = this.editor.getContents()

                this.$emit('update:content', content)
            } catch (err) {
                console.error('Error updating content:', err)
            } finally {
                this.$nextTick(() => {
                    this.isUpdating = false
                })
            }
        },
    },
}
</script>
